import { Viewport } from './viewport';
import { Magnet } from './magnet';
import { Socket } from './socket';

export class Pointer
{
    static #activePointers = {};
    static #fridgeSurface;
    static #lastPinchDistance = 0;
    static #panStart = { x: 0, y: 0 };
    static #scrollStart = { left: 0, top: 0 };
    static #viewport;

    static #isPanning = false;
    static isZooming = false;

    static handlePan (e)
    {
        if (this.#isPanning === false) {
            return;
        }

        Viewport.scrollTo(
            this.#scrollStart.left - (e.clientX - this.#panStart.x),
            this.#scrollStart.top - (e.clientY - this.#panStart.y),
        );
    }

    static startPinch ()
    {
        this.isZooming = true;

        const pointers = Object.values(this.#activePointers);
        this.#lastPinchDistance = Math.hypot(pointers[1].x - pointers[0].x, pointers[1].y - pointers[0].y);
    }

    static handlePinch ()
    {
        const pointers = Object.values(this.#activePointers);

        const distance = Math.hypot(pointers[1].x - pointers[0].x, pointers[1].y - pointers[0].y);
        const midPointX = (pointers[0].x + pointers[1].x) / 2;
        const midPointY = (pointers[0].y + pointers[1].y) / 2;

        Viewport.zoomAndPan(Viewport.zoom * (distance / this.#lastPinchDistance), midPointX, midPointY);
        this.#lastPinchDistance = distance;
    }

    static init (viewport)
    {
        this.#viewport = viewport;
        this.#fridgeSurface = viewport.querySelector('div');

        this.#viewport.addEventListener('pointerdown', (e) => {
            this.setPointer(e);

            if (this.isTwoFingers()) {
                Magnet.drop();
                return this.startPinch();
            }

            // One finger on a magnet — pick it up
            if (this.isMagnet(e)) {
                return Magnet.pickUp(e);
            }

            // One finger on the background — pan
            if (this.isBackground(e)) {
                return this.startPan(e);
            }
        });

        window.addEventListener('pointerup', (event) => {
            this.#handlePointerEnd(event);
        });

        window.addEventListener('pointercancel', (event) => {
            this.#handlePointerEnd(event);
        });

        window.addEventListener('pointermove', (e) => {
            if (this.isActive(e) === false) {
                return;
            }

            this.setPointer(e);

            if (this.isZooming) {
                e.preventDefault();
                return this.handlePinch();
            }

            if (this.#isPanning) {
                e.preventDefault();
                return this.handlePan(e);
            }

            if (Magnet.isActive() && this.isOneFinger()) {
                e.preventDefault();
                Magnet.drag(e);
            }
        });

        window.addEventListener('wheel', (event) => {
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();

                return Viewport.zoomAndPan(Viewport.zoom + (event.deltaY * -0.01), event.clientX, event.clientY);
            }

            const targetMagnet = Magnet.isActive() || (event.target.tagName === 'ARTICLE' ? event.target : null);

            if (targetMagnet) {
                event.preventDefault(); // Stop native scroll/zoom

                let r = parseFloat(targetMagnet.dataset.rotation) || 0;

                // Macbook rotation check: Use the largest delta (X or Y)
                // A typical mouse wheel is just deltaY.
                const scrollAmount = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;

                // Adjust sensitivity (lower numbers mean slower rotation)
                r += scrollAmount > 0 ? 3 : -3;

                Magnet.move(targetMagnet, { r });

                // Sync to server
                Socket.broadcast(
                    targetMagnet.id,
                    r,
                    parseFloat(targetMagnet.style.left),
                    parseFloat(targetMagnet.style.top),
                );
            }
        }, { passive: false });
    }

    static isActive (event)
    {
        return this.#activePointers[event.pointerId];
    }

    static isBackground (event)
    {
        return event.button === 1 || event.target === this.#fridgeSurface;
    }

    static isMagnet (event)
    {
        return event.target.tagName === 'ARTICLE' && event.button !== 1;
    }

    static isOneFinger ()
    {
        return Object.keys(this.#activePointers).length === 1;
    }

    static isTwoFingers ()
    {
        return Object.keys(this.#activePointers).length === 2;
    }

    static removePointer (event)
    {
        delete this.#activePointers[event.pointerId];
    }

    static setPointer (event)
    {
        this.#activePointers[event.pointerId] = { x: event.clientX, y: event.clientY };
    }

    static startPan (event)
    {
        event.preventDefault();

        this.#isPanning = true;
        this.#panStart = { x: event.clientX, y: event.clientY };
        this.#scrollStart = Viewport.getScrollPosition();
        Viewport.setCursor('grabbing');
    }

    static #handlePointerEnd (event)
    {
        Pointer.removePointer(event);
        Pointer.stopPinch();

        const active = Object.values(this.#activePointers).length;
        if (active === 0 && this.#isPanning) {
            this.#isPanning = false;
            Viewport.setCursor('');
            return;
        }

        if (active === 0 && Magnet.isActive()) {
            Magnet.release(event);
        }
    }

    static stopPinch ()
    {
        if (Object.keys(this.#activePointers).length < 2) {
            this.isZooming = false;
        }
    }
}
